import { OrbitControls, CameraShake } from '@react-three/drei'
import { useControls } from 'leva'
import { useRef, useState, useEffect } from 'react'
import { useFrame } from '@react-three/fiber'
import { Particles } from './Particles'
import * as THREE from 'three'

export default function App({ conversationState = 'idle', audioData = null }) {
  // Toggle this to show/hide Leva panel
  const SHOW_CONTROLS = false; // Set to true to show Leva panel
  
  const cameraRef = useRef()
  const orbitControlsRef = useRef()
  const lastLogTime = useRef(0)
  
  // Mobile detection hook
  const [isMobile, setIsMobile] = useState(false)
  
  useEffect(() => {
    const checkIsMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera
      const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i
      const isSmallScreen = window.innerWidth <= 768
      return mobileRegex.test(userAgent) || isSmallScreen
    }
    
    setIsMobile(checkIsMobile())
    
    // Listen for window resize to update mobile detection
    const handleResize = () => {
      setIsMobile(checkIsMobile())
    }
    
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])
  
  // Determine default scale based on device type
  const defaultScale = isMobile ? 80 : 151
  
  const props = SHOW_CONTROLS ? useControls({
    focus: { value: 7, min: 3, max: 7, step: 0.01 },
    speed: { value: 15.6, min: 0.1, max: 100, step: 0.1 },
    aperture: { value: 5.6, min: 1, max: 5.6, step: 0.1 },
    fov: { value: 0, min: 0, max: 200 },
    curl: { value: 0.24, min: 0.01, max: 0.5, step: 0.01 },
    scale: { value: defaultScale, min: 50, max: 300, step: 1, label: 'Particle Area' },
    enabled: { value: true, label: 'Enable Animation' },
    quality: { value: 2, min: 0.25, max: 2, step: 0.25, label: 'Quality' }
  }) : {
    // Default values - responsive to device type
    focus: 7,
    speed: 15.6,
    aperture: 5.6,
    fov: 0,
    curl: 0.24,
    scale: defaultScale,
    enabled: true,
    quality: 2
  };
  
  if (!props.enabled) {
    return null; // Completely disable rendering for performance
  }
  
  // Keep audio data logging but remove all visual reactions
  useFrame((state) => {
    const now = Date.now();
    
    // Log received data every 2 seconds for debugging
    if (now - lastLogTime.current > 2000) {
      console.log('🎯 App.js received:', { 
        conversationState, 
        audioData: audioData ? {
          volume: audioData.volume?.toFixed(2),
          hasData: true
        } : null 
      });
      lastLogTime.current = now;
    }
    
    // Audio data is received and logged, but no visual reactions applied
    // Original particle system runs with default parameters
  });
  
  return (
    <>
      <OrbitControls 
        ref={orbitControlsRef}
        makeDefault 
        autoRotate 
        autoRotateSpeed={0.5} 
        zoomSpeed={0.1}
        enablePan={true}
        enableZoom={true}
        enableRotate={true}
        minDistance={3}
        maxDistance={15}
      />
      {/* <CameraShake 
        yawFrequency={1} 
        maxYaw={0.05} 
        pitchFrequency={1} 
        maxPitch={0.05} 
        rollFrequency={0.5} 
        maxRoll={0.5} 
        intensity={0.2} 
      /> */}
      <Particles 
        {...props} 
        size={512 * props.quality} 
        conversationState={conversationState}
        audioData={audioData}
      />
    </>
  )
}
